import React, { useState, useEffect, useCallback } from 'react';
import { getDoc, doc, collection, getDocs, query, where, documentId, updateDoc, onSnapshot, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { UsersIcon, BookOpenIcon, ChevronDownIcon, CheckCircleIcon, ViewColumnsIcon, FilmIcon, FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import LoadingSpinner from '../components/LoadingSpinner';
import CommunityStories from '../components/CommunityStories';
import CommunityDiscovery from '../components/CommunityDiscovery';
import OrgMessages from '../components/OrgMessages';
import FindYourCommunity from '../components/FindYourCommunity';
import InvitationCard from '../components/InvitationCard';
import GlobalFeedExplainer from '../components/GlobalFeedExplainer';

const MyCommunity = () => {
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null); // 'GLOBAL', null (My Community), or orgId
  const [defaultOrgId, setDefaultOrgId] = useState(null);
  const [globalOrgId, setGlobalOrgId] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ memberCount: 0, discipleCount: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingCommunityData, setLoadingCommunityData] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [viewMode, setViewMode] = useState('stories'); // 'stories' or 'reels'
  const [feedFilter, setFeedFilter] = useState('all'); // 'all' or 'stories_announcements'
  const [orgPrimaryColor, setOrgPrimaryColor] = useState('#5ABA47'); // Default green color
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showGlobalFeedExplainer, setShowGlobalFeedExplainer] = useState(false);
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  // Step 1: Fetch user's orgs and their default preference
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    // Set up a real-time listener on the user's document
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), async (userDoc) => {
      if (!userDoc.exists()) {
        setLoading(false);
        return;
      }

      try {
        const userData = userDoc.data();
        setIsSuperAdmin(userData.isSuperAdmin || false);
        
        // Find global organization
        try {
          const globalOrgQuery = query(
            collection(db, 'organizations'),
            where('type', '==', 'global'),
            limit(1)
          );
          const globalOrgSnapshot = await getDocs(globalOrgQuery);
          if (!globalOrgSnapshot.empty) {
            setGlobalOrgId(globalOrgSnapshot.docs[0].id);
          } else {
            // Fallback: check for isGlobal flag
            const fallbackQuery = query(
              collection(db, 'organizations'),
              where('isGlobal', '==', true),
              limit(1)
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            if (!fallbackSnapshot.empty) {
              setGlobalOrgId(fallbackSnapshot.docs[0].id);
            }
          }
        } catch (error) {
          console.error('Error finding global organization:', error);
        }
        
        const orgIds = userData.activeOrgs || [];
        const userDefaultOrgId = userData.defaultOrganizationId || null;
        setDefaultOrgId(userDefaultOrgId);

        if (orgIds.length > 0) {
          const userOrgsRolesData = {};
          const userOrgsRolesRef = collection(db, 'users', currentUser.uid, 'organizations');
          const userOrgsRolesSnapshot = await getDocs(userOrgsRolesRef);
          userOrgsRolesSnapshot.forEach(doc => {
            userOrgsRolesData[doc.id] = doc.data();
          });

          const orgsQuery = query(collection(db, 'organizations'), where(documentId(), 'in', orgIds));
          const orgsSnapshot = await getDocs(orgsQuery);
          
          const orgs = orgsSnapshot.docs
            .map(d => ({ 
              id: d.id, 
              ...d.data(),
              currentUserRole: userOrgsRolesData[d.id]?.role || 'member' 
            }))
            .sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));

          // Filter out global org for "My Community" view
          const nonGlobalOrgs = orgs.filter(org => org.id !== globalOrgId);
          setUserOrganizations(orgs);
          
          // Preserve selected org if it still exists, otherwise update it
          setSelectedOrg(prevSelectedOrg => {
            const currentSelectedStillExists = orgs.find(o => o.id === prevSelectedOrg?.id);
            if (currentSelectedStillExists) {
              return currentSelectedStillExists;
            }
            if (userDefaultOrgId) {
              return orgs.find(o => o.id === userDefaultOrgId) || orgs[0] || null;
            }
            return orgs[0] || null;
          });
          
          // Set selectedOrgId based on selectedOrg
          if (selectedOrg) {
            setSelectedOrgId(selectedOrg.id);
          } else if (nonGlobalOrgs.length === 0 && globalOrgId) {
            // New user with only global org - default to global feed
            setSelectedOrgId('GLOBAL');
            // Check if explainer should be shown
            const hasDismissed = localStorage.getItem('tom_global_feed_explainer_dismissed');
            if (!hasDismissed) {
              setShowGlobalFeedExplainer(true);
            }
          } else {
            setSelectedOrgId(null); // My Community
          }

        } else {
          // No active orgs, clear the state
          setUserOrganizations([]);
          setSelectedOrg(null);
          setSelectedOrgId(null);
        }
      } catch (error) {
        console.error("[MyCommunity] Error fetching user's organizations:", error);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsub();
  }, [currentUser]);

  // Step 2: Fetch data for the selected organization
  useEffect(() => {
    if (!selectedOrg) {
      setMembers([]);
      setStats({ memberCount: 0, discipleCount: 0 });
      setOrgPrimaryColor('#5ABA47'); // Reset to default green
      console.log('[MyCommunity] No selectedOrg, clearing members/stats');
      return;
    }
    console.log('[MyCommunity] Fetching community data for org:', selectedOrg);
    const fetchCommunityData = async () => {
      setLoadingCommunityData(true);
      try {
        // Fetch organization settings to get primary color
        const orgDoc = await getDoc(doc(db, 'organizations', selectedOrg.id));
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const primaryColor = orgData?.settings?.primaryColor || '#5ABA47';
          setOrgPrimaryColor(primaryColor);
          console.log('[MyCommunity] Organization primary color:', primaryColor);
        } else {
          setOrgPrimaryColor('#5ABA47'); // Fallback to default green
        }

        const membersQuery = query(collection(db, 'users'), where('activeOrgs', 'array-contains', selectedOrg.id));
        const membersSnapshot = await getDocs(membersQuery);
        const memberList = membersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setMembers(memberList);
        const memberCount = memberList.length;
        const discipleCount = memberList.reduce((sum, member) => sum + (member.discipleIds?.length || 0), 0);
        const newStats = { memberCount, discipleCount };
        setStats(newStats);
        console.log('[MyCommunity] Members:', memberList);
        console.log('[MyCommunity] Stats:', newStats);
      } catch (error) {
        console.error(`[MyCommunity] Error fetching data for ${selectedOrg.displayName}:`, error);
        setOrgPrimaryColor('#5ABA47'); // Fallback to default green on error
      }
      setLoadingCommunityData(false);
    };
    fetchCommunityData();
  }, [selectedOrg]);

  // Load pending invitations
  useEffect(() => {
    if (!currentUser?.email || selectedOrgId !== null) {
      setPendingInvitations([]);
      return;
    }

    const loadPendingInvitations = async () => {
      try {
        const userEmail = currentUser.email.toLowerCase();
        const orgsSnapshot = await getDocs(collection(db, 'organizations'));
        const invitations = [];

        for (const orgDoc of orgsSnapshot.docs) {
          try {
            const invitesRef = collection(db, 'organizations', orgDoc.id, 'invitations');
            const invitesQuery = query(
              invitesRef,
              where('email', '==', userEmail),
              where('status', '==', 'pending')
            );
            const invitesSnapshot = await getDocs(invitesQuery);

            invitesSnapshot.docs.forEach(inviteDoc => {
              const inviteData = inviteDoc.data();
              invitations.push({
                id: inviteDoc.id,
                organizationId: orgDoc.id,
                organizationName: orgDoc.data().name || orgDoc.data().displayName || 'Organization',
                inviterName: inviteData.inviterName || 'Someone',
                createdAt: inviteData.createdAt,
                expiresAt: inviteData.expiresAt,
                status: inviteData.status
              });
            });
          } catch (error) {
            console.error(`Error loading invitations for org ${orgDoc.id}:`, error);
          }
        }

        setPendingInvitations(invitations);
      } catch (error) {
        console.error('Error loading pending invitations:', error);
      }
    };

    loadPendingInvitations();
  }, [currentUser?.email, selectedOrgId]);

  // Handle invitation accept/decline
  const handleAcceptInvitation = async (invitationId, orgId) => {
    try {
      const inviteRef = doc(db, 'organizations', orgId, 'invitations', invitationId);
      await updateDoc(inviteRef, { status: 'accepted' });
      
      // Add user to organization (you may need to adjust this based on your org structure)
      const userOrgRef = doc(db, 'users', currentUser.uid, 'organizations', orgId);
      await updateDoc(userOrgRef, { status: 'active' }, { merge: true });
      
      // Refresh organizations
      window.location.reload();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation. Please try again.');
    }
  };

  const handleDeclineInvitation = async (invitationId, orgId) => {
    try {
      const inviteRef = doc(db, 'organizations', orgId, 'invitations', invitationId);
      await updateDoc(inviteRef, { status: 'declined' });
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Failed to decline invitation. Please try again.');
    }
  };

  const handleContinueGlobalFeed = (dontShowAgain) => {
    setShowGlobalFeedExplainer(false);
  };

  // function to set the default organization
  const handleSetDefault = async () => {
    if (!currentUser || !selectedOrg) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        defaultOrganizationId: selectedOrg.id
      });
      setDefaultOrgId(selectedOrg.id);
    } catch (error) {
      console.error("[MyCommunity] Error setting default organization:", error);
    }
  };

  // Helper function to darken a hex color for hover effect
  const darkenColor = (hex, percent = 15) => {
    if (!hex || !hex.startsWith('#')) return '#4a9a3a'; // Fallback to default green
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, Math.floor((num >> 16) * (1 - percent / 100)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) * (1 - percent / 100)));
    const b = Math.max(0, Math.floor((num & 0x0000FF) * (1 - percent / 100)));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  const OrgHeader = () => {
    if (!selectedOrg) return null;

    return (
      <div className="w-full bg-white flex flex-row items-center gap-3 pt-6 pb-4 px-3 md:px-6" style={{margin:0}}>
        {/* Logo */}
        <img src={selectedOrg.logoUrl} alt={`${selectedOrg.displayName} logo`} className="h-12 w-12 object-cover rounded-full flex-shrink-0 ml-0" />
        {/* Title and subtitle */}
        <div className="flex flex-col justify-center min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate" style={{lineHeight:'1.1'}}>{selectedOrg.displayName}</h1>
            {userOrganizations.length > 1 && (
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                  <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                </Menu.Button>
                <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-left bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="px-1 py-1 ">
                    {userOrganizations.map(org => (
                      <Menu.Item key={org.id}>
                        {({ active }) => (
                          <button
                            onClick={() => setSelectedOrg(org)}
                            className={`$
                              {active ? 'bg-blue-500 text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}
                          >
                            {org.displayName}
                            {org.id === selectedOrg.id && <CheckCircleIcon className="h-5 w-5 ml-auto text-blue-500"/>}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                  {selectedOrg.id !== defaultOrgId && (
                    <div className="px-1 py-1">
                      <Menu.Item>
                        {({ active }) => (
                           <button
                              onClick={handleSetDefault}
                              className={`$
                                {active ? 'bg-blue-500 text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}
                            >
                              Set as Default
                            </button>
                        )}
                      </Menu.Item>
                    </div>
                  )}
                </Menu.Items>
              </Menu>
            )}
          </div>
          <span className="text-gray-500 text-sm truncate max-w-xs" style={{fontWeight:400}}>{selectedOrg.bio}</span>
        </div>
        {/* Spacer */}
        <div className="flex-1" />
        {(isSuperAdmin || selectedOrg?.currentUserRole === 'admin') && (
          <Link 
            to={`/organization/${selectedOrg.id}`} 
            className="text-xs px-3 py-1.5 text-white rounded-md transition-colors ml-auto whitespace-nowrap"
            style={{
              backgroundColor: orgPrimaryColor,
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = darkenColor(orgPrimaryColor);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = orgPrimaryColor;
            }}
          >
            Admin
          </Link>
        )}
      </div>
    );
  };
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  }

  // Filter out global org for display
  const nonGlobalOrgs = userOrganizations.filter(org => org.id !== globalOrgId);
  const hasOnlyGlobalOrg = nonGlobalOrgs.length === 0 && userOrganizations.length > 0 && globalOrgId && userOrganizations.some(org => org.id === globalOrgId);

  if (userOrganizations.length === 0 || hasOnlyGlobalOrg) {
    return (
      <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        {showGlobalFeedExplainer ? (
          <GlobalFeedExplainer onContinue={handleContinueGlobalFeed} />
        ) : selectedOrgId === 'GLOBAL' ? (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Together On Mission</h1>
              <CommunityStories organizationId={globalOrgId} viewMode={viewMode} />
            </div>
          </div>
        ) : (
          <>
            {pendingInvitations.length > 0 && (
              <div className="max-w-2xl mx-auto mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invitations ({pendingInvitations.length})</h2>
                {pendingInvitations.map(invitation => (
                  <InvitationCard
                    key={invitation.id}
                    invitation={invitation}
                    onAccept={handleAcceptInvitation}
                    onDecline={handleDeclineInvitation}
                  />
                ))}
              </div>
            )}
            <FindYourCommunity onBrowseOrganizations={() => navigate('/discover-communities')} />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <OrgHeader />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {selectedOrg ? (
          <div className="space-y-8">
            {loadingCommunityData ? (
              <div className="flex justify-center py-10"><LoadingSpinner /></div>
            ) : (
              <>
                {/* Organization Messages/Announcements - Full Width */}
                <div className="w-full">
                  <OrgMessages organizationId={selectedOrg.id} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Stories Component (shows on top on mobile) */}
                  <div className="lg:col-span-2">
                    {/* View Mode Toggle and Filter */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                      <h2 className="text-xl font-semibold text-gray-800">Community Stories</h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Filter Dropdown */}
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <FunnelIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">
                              {feedFilter === 'all' ? 'All Content' : 'Stories & Announcements'}
                            </span>
                            <ChevronDownIcon className="h-4 w-4" />
                          </Menu.Button>
                          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                            <div className="px-1 py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => setFeedFilter('all')}
                                    className={`${
                                      active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                    } group flex rounded-md items-center w-full px-2 py-2 text-sm`}
                                  >
                                    {feedFilter === 'all' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
                                    All Content
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => setFeedFilter('stories_announcements')}
                                    className={`${
                                      active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                    } group flex rounded-md items-center w-full px-2 py-2 text-sm`}
                                  >
                                    {feedFilter === 'stories_announcements' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
                                    Stories & Announcements
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Menu>
                        {/* View Mode Toggle */}
                        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setViewMode('stories')}
                            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              viewMode === 'stories' 
                                ? 'bg-white text-gray-900 shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <ViewColumnsIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Stories</span>
                          </button>
                          <button
                            onClick={() => setViewMode('reels')}
                            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              viewMode === 'reels' 
                                ? 'bg-white text-gray-900 shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <FilmIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Reels</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <CommunityStories organizationId={selectedOrg.id} viewMode={viewMode} />
                  </div>

                  {/* Right column with Stats and Members */}
                  <div className="lg:col-span-1 space-y-8">
                    {/* Stats */}
                    <div className="grid grid-cols-1 gap-6">
                      <div className="bg-white p-6 rounded-lg shadow flex items-center">
                        <UsersIcon className="h-10 w-10 text-blue-500 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-500 truncate">Total Members</dt>
                          <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.memberCount}</dd>
                        </div>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow flex items-center">
                        <BookOpenIcon className="h-10 w-10 text-green-500 mr-4" />
                        <div>
                          <dt className="text-sm font-medium text-gray-500 truncate">Total Disciples</dt>
                          <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.discipleCount}</dd>
                        </div>
                      </div>
                    </div>

                    {/* Members List */}
                    <div className="bg-white p-6 rounded-lg shadow">
                      <h3 className="text-xl font-semibold mb-4 text-gray-800">Community Members</h3>
                      <ul className="space-y-4 max-h-96 overflow-y-auto">
                        {members.map(member => (
                          <li key={member.id} className="flex items-center space-x-3">
                            <img className="h-10 w-10 rounded-full object-cover" src={member.photoURL || `https://ui-avatars.com/api/?name=${member.name}&background=random`} alt="" />
                            <span className="text-sm font-medium text-gray-700">{member.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow">
             <h2 className="text-2xl font-bold text-gray-700">Welcome to Your Communities</h2>
             <p className="text-gray-500 mt-2">Please select one of your communities to get started.</p>
           </div>
        )}
      </div>
    </div>
  );
};

export default MyCommunity; 